import React, { useState } from 'react';
import { Button, ButtonProps } from 'reactstrap';
import { useTranslation } from '../translations';
import { captureError } from '../utils/errorHandling';
import { MaterialIcon } from './MaterialIcon';

interface ActionButtonProps extends ButtonProps {
  readonly action: () => Promise<void>;
  readonly onSuccess: () => void;
  readonly onFailure?: (err: Error) => void;
}

export const ActionButton = ({
  action,
  onSuccess,
  onFailure,
  disabled,
  children,
  ...rest
}: ActionButtonProps): React.JSX.Element => {
  const { ts } = useTranslation();
  const [requestPending, setRequestPending] = useState(false);

  const submitForm = async () => {
    if (requestPending) {
      return;
    }

    setRequestPending(true);

    try {
      await action();

      setRequestPending(false);

      onSuccess();
    } catch (error) {
      setRequestPending(false);
      if (onFailure) {
        onFailure(error as Error);
      } else {
        captureError(error as Error);
      }
    }
  };

  return (
    <Button {...rest} disabled={disabled ?? requestPending} onClick={submitForm}>
      {!requestPending && (children || 'Submit')}
      {requestPending && (
        <>
          <MaterialIcon name="sync" /> {ts('common.action_button.updating')}
        </>
      )}
    </Button>
  );
};
